//React
import { useEffect, useRef, useState } from 'react';

//Mantine
import { Button, Card, Flex, Tooltip } from '@mantine/core';

//Components
import { Section, SectionProps } from './Section';
import { PatientSearchComponent } from 'pages/claims/components/ModalComponents/ClaimPatientSearch';

//Hooks
import { useGetPatient } from 'api/resource';
import { useGetModalSectionBorderColor } from 'hooks/useGetModalSectionBorderColor';

//Interfaces
import { Patient } from 'interfaces';

//Icons
import { IconUser, IconX } from '@tabler/icons-react';
import { useActiveForm } from 'pages/claims/components';
import { getValueByPath } from 'utils/getValueByPath';

interface ClaimPatientSectionProps extends SectionProps {
  isNewPatient: boolean;
  setIsNewPatient?: (value: boolean) => void;
  claimIndex?: number;
}

export const ClaimPatientSection = ({
  index,
  title,
  keys,
  cols,
  editComponentsMap,
  type = 'edit',
  isNewPatient,
  setIsNewPatient,
}: ClaimPatientSectionProps) => {
  const borderColor = useGetModalSectionBorderColor({ keys });
  const { form, prefix } = useActiveForm();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const path = prefix ? `${prefix}.patient` : 'patient';
  const pathID = prefix ? `${prefix}.patientID` : 'patientID';
  const healthcardPath = prefix ? `${prefix}.healthcard` : 'healthcard';
  const healthcardIDPath = prefix ? `${prefix}.healthcardID` : 'healthcardID';
  const payorPath = prefix ? `${prefix}.payor` : 'payor';
  const isNewPatientPath = prefix ? `${prefix}.isNewPatient` : 'isNewPatient';

  const existingPatient = getValueByPath(form.getValues(), path);

  // Fetch patient data if not a new patient
  const patientID = isNewPatient ? undefined : existingPatient?.id;

  // need to refetch to get healthcard data
  // could refactor to return healthcard data in patientSearch
  // or could add a getHealthcardsByPatientID call
  const { data: fetchedPatient, isLoading } = useGetPatient({ id: patientID });

  useEffect(() => {
    if (!isNewPatient && fetchedPatient?.id && type !== 'edit') {
      const currentValues = form.getValues();
      const currentHealthcardID = getValueByPath(
        currentValues,
        healthcardIDPath
      );
      const selectedHealthcard = fetchedPatient.healthcards?.find(
        (healthcard) => healthcard.id === currentHealthcardID
      );
      const payorProvince = getValueByPath(
        currentValues,
        payorPath
      )?.province?.toUpperCase();
      const matchingHealthcard = fetchedPatient.healthcards?.find(
        (healthcard) =>
          Boolean(payorProvince) &&
          healthcard?.province?.toUpperCase() === payorProvince
      );
      const defaultHealthcard =
        matchingHealthcard ?? fetchedPatient.healthcards?.[0] ?? null;
      const resolvedHealthcard = currentHealthcardID
        ? selectedHealthcard ?? null
        : defaultHealthcard;

      form.setFieldValue(path, fetchedPatient);
      form.setFieldValue(pathID, fetchedPatient.id);
      setSelectedPatient(fetchedPatient);
      form.setFieldValue(healthcardPath, resolvedHealthcard);
      form.setFieldValue(healthcardIDPath, resolvedHealthcard?.id ?? null);
    }
  }, [fetchedPatient]);

  const resetPatient = () => {
    setSelectedPatient(null);
    form.setFieldValue(path, null);
    form.setFieldValue(pathID, null);
    form.setFieldValue(healthcardPath, null);
    form.setFieldValue(healthcardIDPath, null);
    form.setFieldValue(isNewPatientPath, false);
    setIsNewPatient(false);
    requestAnimationFrame(() => cardRef.current?.focus());
  };

  return (
    <Card
      ref={cardRef}
      tabIndex={-1}
      padding="md"
      shadow="xs"
      radius="md"
      withBorder
      style={{
        borderColor,
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      {isNewPatient || fetchedPatient || existingPatient ? (
        <Section
          index={index}
          title={title}
          icon={<IconUser size={20} />}
          keys={keys}
          cols={cols}
          editComponentsMap={editComponentsMap}
          type={type}
          isLoading={isLoading}
        />
      ) : (
        <PatientSearchComponent
          setIsNewPatient={setIsNewPatient}
          patientPath={path}
          patientIDPath={pathID}
          healthcardPath={healthcardPath}
          healthcardIDPath={healthcardIDPath}
          payorPath={payorPath}
          isNewPatientPath={isNewPatientPath}
          cardRef={cardRef}
        />
      )}
      {(selectedPatient || isNewPatient || existingPatient) && (
        <Flex justify={'center'} w="2%" gap={'lg'} mb={'md'}>
          <Tooltip label="Change Patient" position="top">
            <Button variant="subtle" w={'100%'} p={0} c="gray">
              <IconX size={20} onClick={resetPatient} />
            </Button>
          </Tooltip>
        </Flex>
      )}
    </Card>
  );
};
